/* NS POPUP NO PAGE JUMP v6 - no body repositioning, no scroll reset.
   Replaces the standard notification UI; preserves Shopify's product-form API.
   Quantity changes address the exact existing cart line, never add it again. */
if (!customElements.get('cart-notification')) {
  class CartNotification extends HTMLElement {
    constructor() {
      super();
      this.notification = this.querySelector('#cart-notification');
      this.header = document.querySelector('sticky-header');
      this.root = window.Shopify?.routes?.root || '/';
      this.cartURL = this.querySelector('#cart-notification-button')?.getAttribute('href') || this.root + 'cart';
      this.pointerMode = window.nsPopupInputMode !== 'keyboard';
      this.sequence = 0;
      this.copy = document.documentElement.lang.toLowerCase().startsWith('es') ? {
        title: 'Añadido al carrito', quantity: 'Cantidad en el carrito', minus: 'Reducir cantidad', plus: 'Aumentar cantidad',
        checkout: 'Ir al pago', view: 'Ver carrito', continue: 'Seguir comprando', close: 'Cerrar',
        error: 'No pudimos actualizar la cantidad. Revisa tu carrito antes de continuar.'
      } : {
        title: 'Added to cart', quantity: 'Quantity in cart', minus: 'Decrease quantity', plus: 'Increase quantity',
        checkout: 'Checkout', view: 'View cart', continue: 'Continue shopping', close: 'Close',
        error: 'We could not update the quantity. Check your cart before continuing.'
      };
      this.notification?.classList.add('ns-legacy-cart-notification');
      document.addEventListener('pointerdown', () => {
        this.pointerMode = true;
        window.nsPopupInputMode = 'pointer';
        this.dialog?.classList.add('ns-buy-quantity--pointer');
      }, { capture: true, passive: true });
      document.addEventListener('keydown', event => {
        if (['Shift', 'Control', 'Alt', 'Meta'].includes(event.key)) return;
        this.pointerMode = false;
        window.nsPopupInputMode = 'keyboard';
        this.dialog?.classList.remove('ns-buy-quantity--pointer');
        this.activeElement?.removeAttribute('data-ns-cart-pointer-focus');
      }, true);
      window.addEventListener('pageshow', () => {
        if (this.dialog?.open) this.close();
        this.navigating = false;
      });
    }

    getSectionsToRender() {
      return [
        { id: 'cart-notification-product', selector: `[id="cart-notification-product-${this.cartItemKey}"]` },
        { id: 'cart-notification-button' }, { id: 'cart-icon-bubble' }
      ];
    }
    getSectionInnerHTML(html, selector = '.shopify-section') {
      if (typeof html !== 'string') return '';
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML || '';
    }
    applySections(sections) {
      if (!sections) return;
      for (const section of this.getSectionsToRender()) {
        const target = document.getElementById(section.id);
        if (!target || typeof sections[section.id] !== 'string') continue;
        const parsed = new DOMParser().parseFromString(sections[section.id], 'text/html');
        const content = parsed.querySelector(section.selector || '.shopify-section');
        if (content) target.innerHTML = content.innerHTML;
      }
    }
    setActiveElement(element) {
      this.activeElement = element;
      if (window.nsPopupInputMode) this.pointerMode = window.nsPopupInputMode === 'pointer';
    }

    createDialog() {
      const dialog = document.createElement('dialog');
      dialog.className = 'ns-buy-quantity ns-cart-confirmation';
      dialog.setAttribute('aria-labelledby', 'ns-cart-confirmation-title');
      dialog.innerHTML = '<button type="button" class="ns-buy-quantity__close"><svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false"><path d="M6 6 18 18M18 6 6 18" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"/></svg></button>' +
        '<h2 id="ns-cart-confirmation-title"></h2><img class="ns-buy-quantity__image" width="88" height="88" alt="" hidden><p class="ns-buy-quantity__product"></p><p class="ns-cart-confirmation__details"></p>' +
        '<label class="ns-cart-confirmation__quantity-label" for="ns-cart-confirmation-quantity"></label>' +
        '<div class="ns-buy-quantity__stepper"><button type="button" data-step="-1">−</button><input id="ns-cart-confirmation-quantity" type="number" value="1" min="1" step="1" max="9007199254740991" inputmode="numeric" required><button type="button" data-step="1">+</button></div>' +
        '<div class="ns-cart-confirmation__actions"><a class="ns-cart-confirmation__view"></a><form method="post"><button type="submit" name="checkout" class="ns-buy-quantity__checkout"></button></form></div>' +
        '<button type="button" class="ns-cart-confirmation__continue"></button><p class="ns-cart-confirmation__status" role="status" aria-live="polite"></p>';
      const panel = document.createElement('div');
      panel.className = 'ns-popup-panel';
      while (dialog.firstChild) panel.append(dialog.firstChild);
      dialog.append(panel);
      this.dialog = dialog;
      this.input = dialog.querySelector('input');
      this.minus = dialog.querySelector('[data-step="-1"]');
      this.plus = dialog.querySelector('[data-step="1"]');
      this.status = dialog.querySelector('.ns-cart-confirmation__status');
      this.view = dialog.querySelector('a');
      this.image = dialog.querySelector('img');
      dialog.querySelector('h2').textContent = this.copy.title;
      dialog.querySelector('label').textContent = this.copy.quantity;
      const close = dialog.querySelector('.ns-buy-quantity__close');
      close.setAttribute('aria-label', this.copy.close);
      close.addEventListener('click', () => this.close());
      this.minus.setAttribute('aria-label', this.copy.minus);
      this.plus.setAttribute('aria-label', this.copy.plus);
      this.image.addEventListener('error', () => { this.image.hidden = true; });
      const form = dialog.querySelector('form');
      form.action = this.cartURL;
      form.querySelector('button').textContent = this.copy.checkout;
      form.addEventListener('submit', async event => {
        event.preventDefault();
        if (this.navigating) return;
        this.navigating = true;
        if (await this.commitInput()) {
          // Native submit() omits submitter; preserve Shopify's checkout field.
          if (!form.querySelector('input[name="checkout"]')) {
            const checkout = document.createElement('input');
            checkout.type = 'hidden'; checkout.name = 'checkout'; checkout.value = 'Checkout'; form.append(checkout);
          }
          HTMLFormElement.prototype.submit.call(form);
        } else this.navigating = false;
      });
      this.view.href = this.cartURL;
      this.view.textContent = this.copy.view;
      this.view.addEventListener('click', async event => {
        event.preventDefault();
        if (this.navigating) return;
        this.navigating = true;
        // View cart remains an escape route even after an update/network error.
        await this.commitInput();
        window.location.assign(this.cartURL);
      });
      const keepShopping = dialog.querySelector('.ns-cart-confirmation__continue');
      keepShopping.textContent = this.copy.continue;
      keepShopping.addEventListener('click', () => this.close());
      for (const button of [this.minus, this.plus]) button.addEventListener('click', () => {
        if (this.busy || !this.item) return;
        const current = this.item.quantity;
        const step = Number(this.input.step) || 1;
        this.input.value = String(button === this.plus ? current + step : Math.max(Number(this.input.min), current - step));
        this.commitInput();
      });
      this.input.addEventListener('change', () => this.commitInput());
      this.input.addEventListener('keydown', event => {
        if (event.key === 'Enter') { event.preventDefault(); this.commitInput(); }
      });
      dialog.addEventListener('click', event => {
        if (event.target !== dialog) return;
        if (getComputedStyle(panel).display === 'block') { this.close(); return; }
        const rect = dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.close();
      });
      dialog.addEventListener('close', () => {
        if (this.activeElement?.isConnected) {
          if (this.pointerMode) this.activeElement.setAttribute('data-ns-cart-pointer-focus', '');
          this.activeElement.focus({ preventScroll: true });
        }
        nsEnsurePopupViewport().release(dialog);
      });
      document.body.append(dialog);
    }

    open() {
      if (!window.HTMLDialogElement || typeof HTMLDialogElement.prototype.showModal !== 'function') {
        window.location.assign(this.cartURL);
        return;
      }
      if (!this.dialog) this.createDialog();
      this.dialog.classList.toggle('ns-buy-quantity--pointer', this.pointerMode);
      this.navigating = false;
      if (!this.dialog.open) {
        nsEnsurePopupViewport().lock(this.dialog);
        try { this.dialog.showModal(); }
        catch (error) { nsEnsurePopupViewport().release(this.dialog); throw error; }
      }
      this.dialog.querySelector('.ns-buy-quantity__close').focus({ preventScroll: true });
    }
    close() { if (this.dialog?.open) this.dialog.close(); }
    handleBodyClick() {} // Compatibility only; native dialog owns the backdrop.

    renderContents(state) {
      // product-form calls this only after Shopify confirms the original add.
      this.sequence++;
      const previous = this.busy ? this.pending : null;
      this.cartItemKey = state.key;
      this.item = state;
      this.applySections(state.sections);
      if (!window.HTMLDialogElement || typeof HTMLDialogElement.prototype.showModal !== 'function') { this.open(); return; }
      if (!this.dialog) this.createDialog();
      this.setStatus('');
      // Shopify add.js already returns the TOTAL quantity of the affected line.
      // Render once, while closed, rather than show/repaint after an extra GET.
      if (!previous && state.key && Number.isSafeInteger(state.quantity) && state.quantity > 0) {
        this.paintItem(state);
        this.setBusy(false);
        this.pending = null;
        this.open();
        return;
      }
      const sequence = this.sequence;
      this.setBusy(true);
      this.pending = (async () => {
        if (previous) await previous;
        if (sequence !== this.sequence) return false;
        this.setBusy(true);
        try {
          const cart = await this.readCart();
          if (sequence !== this.sequence) return false;
          const item = this.findItem(cart, state);
          if (!item) throw new Error('Cart line no longer exists');
          this.item = item;
          this.cartItemKey = item.key;
          this.paintItem(item);
          this.setStatus('');
          return true;
        } catch (_) {
          if (sequence !== this.sequence) return false;
          // Add response contains total line quantity; safe fallback if GET fails.
          if (Number.isSafeInteger(state.quantity) && state.quantity > 0 && state.key) { this.paintItem(state); this.setStatus(''); return true; }
          this.item = null;
          this.setStatus(this.copy.error, true);
          return false;
        } finally {
          if (sequence === this.sequence) {
            this.setBusy(false);
            this.open();
          }
        }
      })();
    }
    async readCart() {
      const response = await fetch(this.root + 'cart.js', { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Cart read failed');
      return response.json();
    }
    findItem(cart, previous) {
      const exact = cart.items?.find(item => item.key === previous.key);
      if (exact) return exact;
      const properties = value => JSON.stringify(Object.entries(value || {}).sort(([a], [b]) => a.localeCompare(b)));
      const matches = cart.items?.filter(item => String(item.variant_id || item.id) === String(previous.variant_id || previous.id) &&
        properties(item.properties) === properties(previous.properties) &&
        String(item.selling_plan_allocation?.selling_plan?.id || '') === String(previous.selling_plan_allocation?.selling_plan?.id || '')) || [];
      // Do not guess between split lines or variants with different properties/plans.
      return matches.length === 1 ? matches[0] : null;
    }
    paintItem(item) {
      const title = this.dialog.querySelector('.ns-buy-quantity__product');
      const text = item.title || item.product_title || '';
      if (title.textContent !== text) title.textContent = text;
      const details = [];
      if (item.selling_plan_allocation?.selling_plan?.name) details.push(item.selling_plan_allocation.selling_plan.name);
      for (const [key, value] of Object.entries(item.properties || {})) {
        if (!key.startsWith('_') && value) details.push(key + ': ' + String(value).split('/uploads/').pop());
      }
      const detail = this.dialog.querySelector('.ns-cart-confirmation__details');
      if (detail.textContent !== details.join(' · ')) detail.textContent = details.join(' · ');
      let image = item.featured_image?.url || item.image;
      let cachedImage = false;
      const card = this.activeElement?.closest('.card-wrapper');
      const cardVariant = card?.querySelector('form input[name="id"]')?.value;
      if (cardVariant && String(cardVariant) === String(item.variant_id || item.id)) {
        const loaded = card.querySelector('.card__media img, .card__inner .media img');
        if (loaded?.complete && loaded.naturalWidth > 0) {
          image = loaded.currentSrc || loaded.src;
          cachedImage = true;
        }
      }
      if (typeof image === 'string' && /^(https?:\/\/|\/)/.test(image)) {
        const url = new URL(image, window.location.origin);
        if (!cachedImage && (url.hostname === 'cdn.shopify.com' || (url.origin === window.location.origin && url.pathname.startsWith('/cdn/shop/')))) url.searchParams.set('width', '176');
        if (this.image.getAttribute('src') !== url.href) {
          this.image.src = url.href;
          this.image.hidden = false;
        }
      } else if (this.image.hasAttribute('src')) {
        this.image.hidden = true;
        this.image.removeAttribute('src');
      }
      const rule = item.quantity_rule || {};
      this.input.min = String(Math.max(1, rule.min || 1));
      this.input.step = String(Math.max(1, rule.increment || 1));
      this.input.max = String(rule.max ?? 9007199254740991);
      this.input.value = String(item.quantity || 1);
      this.sync();
    }
    setStatus(message, error = false) {
      this.status.textContent = message;
      this.status.setAttribute('role', error ? 'alert' : 'status');
    }
    setBusy(busy) {
      this.busy = busy;
      this.dialog.setAttribute('aria-busy', String(busy));
      this.input.disabled = busy || !this.item;
      this.sync();
    }
    sync() {
      this.minus.disabled = this.busy || !this.item || Number(this.input.value) <= Number(this.input.min);
      this.plus.disabled = this.busy || !this.item || Number(this.input.value) >= Number(this.input.max);
    }
    async commitInput() {
      if (this.pending && this.busy) { if (!await this.pending) return false; }
      if (!this.item || !this.input.reportValidity()) return false;
      const quantity = this.input.valueAsNumber;
      if (!Number.isSafeInteger(quantity) || quantity < 1) return false;
      if (quantity === this.item.quantity) return true;
      const previous = this.item, sequence = this.sequence;
      this.setBusy(true);
      this.pending = (async () => {
        try {
          const response = await fetch(this.root + 'cart/change.js', {
            method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: previous.key, quantity, sections: this.getSectionsToRender().map(section => section.id), sections_url: window.location.pathname })
          });
          const cart = await response.json();
          if (!response.ok || cart.status) throw new Error('Quantity update rejected');
          const item = this.findItem(cart, previous);
          if (!item) throw new Error('Cart line changed');
          if (sequence === this.sequence) {
            this.item = item;
            this.cartItemKey = item.key;
            this.paintItem(item);
            this.applySections(cart.sections);
            this.setStatus('');
          }
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-notification', productVariantId: item.variant_id || item.id, cartData: cart });
          }
          return true;
        } catch (_) {
          // A network failure may occur after the server accepted the change.
          // Re-read before permitting another quantity write; never retry POST.
          if (sequence === this.sequence) {
            try {
              const cart = await this.readCart();
              this.item = this.findItem(cart, previous);
              if (this.item) { this.cartItemKey = this.item.key; this.paintItem(this.item); }
              try {
                const response = await fetch(this.cartURL + '?sections=cart-icon-bubble,cart-notification-button', { credentials: 'same-origin', cache: 'no-store' });
                if (response.ok) this.applySections(await response.json());
              } catch (_) { /* Keep the error visible if section refresh also fails. */ }
            } catch (_) { this.item = null; }
            this.setStatus(this.copy.error, true);
          }
          return false;
        } finally { if (sequence === this.sequence) this.setBusy(false); }
      })();
      return this.pending;
    }
  }
  customElements.define('cart-notification', CartNotification);
}

/* Shared input lock: no page repositioning, overflow toggles or scroll reset. */
function nsEnsurePopupViewport() {
  if (window.nsPopupViewport) return window.nsPopupViewport;
  const active = new Set();
  let touch;
  function update() {
    const vv = window.visualViewport;
    for (const d of active) {
      d.style.setProperty('--ns-popup-vv-height', (vv?.height || window.innerHeight) + 'px');
      d.style.setProperty('--ns-popup-vv-width', (vv?.width || window.innerWidth) + 'px');
      d.style.setProperty('--ns-popup-vv-top', (vv?.offsetTop || 0) + 'px');
      d.style.setProperty('--ns-popup-vv-left', (vv?.offsetLeft || 0) + 'px');
    }
  }
  function canScroll(target, dx, dy) {
    const d = Array.from(active).at(-1);
    if (!d || !d.contains(target)) return false;
    for (let n = target instanceof Element ? target : target.parentElement; n && d.contains(n); n = n.parentElement) {
      const s = getComputedStyle(n);
      if (dy && /auto|scroll/.test(s.overflowY) && n.scrollHeight > n.clientHeight + 1 &&
          ((dy < 0 && n.scrollTop > 0) || (dy > 0 && n.scrollTop < n.scrollHeight - n.clientHeight - 1))) return true;
      if (dx && /auto|scroll/.test(s.overflowX) && n.scrollWidth > n.clientWidth + 1 &&
          ((dx < 0 && n.scrollLeft > 0) || (dx > 0 && n.scrollLeft < n.scrollWidth - n.clientWidth - 1))) return true;
    }
    return false;
  }
  function wheel(e) {
    if (!e.ctrlKey && !canScroll(e.target, e.deltaX, e.deltaY) && e.cancelable) e.preventDefault();
  }
  function start(e) {
    touch = e.touches.length === 1 ? { x: e.touches[0].clientX, y: e.touches[0].clientY } : null;
  }
  function move(e) {
    if (!touch || e.touches.length !== 1) return;
    const p = e.touches[0], dx = touch.x - p.clientX, dy = touch.y - p.clientY;
    touch = { x: p.clientX, y: p.clientY };
    if (!canScroll(e.target, dx, dy) && e.cancelable) e.preventDefault();
  }
  function key(e) {
    const t = e.target;
    if (t instanceof Element && t.closest('input,textarea,select,[contenteditable="true"]')) return;
    if (e.key === ' ' && t instanceof Element && t.closest('button,a,summary')) return;
    if (['ArrowUp','ArrowDown','PageUp','PageDown','Home','End',' '].includes(e.key) &&
        !canScroll(t, 0, ['ArrowUp','PageUp','Home'].includes(e.key) || (e.key === ' ' && e.shiftKey) ? -1 : 1)) e.preventDefault();
  }
  window.nsPopupViewport = {
    lock(d) {
      if (active.has(d)) return;
      if (!active.size) {
        document.addEventListener('wheel', wheel, { capture: true, passive: false });
        document.addEventListener('touchstart', start, { capture: true, passive: true });
        document.addEventListener('touchmove', move, { capture: true, passive: false });
        document.addEventListener('keydown', key, true);
        window.addEventListener('resize', update);
        window.visualViewport?.addEventListener('resize', update);
        window.visualViewport?.addEventListener('scroll', update);
      }
      active.add(d);
      update();
    },
    release(d) {
      if (!active.delete(d)) return;
      for (const p of ['height','width','top','left']) d.style.removeProperty('--ns-popup-vv-' + p);
      if (active.size) return;
      document.removeEventListener('wheel', wheel, true);
      document.removeEventListener('touchstart', start, true);
      document.removeEventListener('touchmove', move, true);
      document.removeEventListener('keydown', key, true);
      window.removeEventListener('resize', update);
      window.visualViewport?.removeEventListener('resize', update);
      window.visualViewport?.removeEventListener('scroll', update);
      touch = null;
    }
  };
  return window.nsPopupViewport;
}
